/*
  Gantt time scale (pure functions; components/ui/gantt uses them, pages may too).

    const scale = buildTimeScale({ start: '2026-08-01', end: '2026-12-31', zoom: 'month' });
    scale.toX('2026-09-30')          // px from the left edge of the timeline
    scale.ticks                       // [{ key, label, day, x, width }] header cells (weeks / months / quarters)
    scale.groups                      // upper header row (months above weeks in week zoom), else []
    scale.width, scale.pxPerDay, scale.startDay, scale.endDay

  Dates are calendar days (no time zone shifts): 'YYYY-MM-DD', Date pickers' local midnight, '16 Sep 2026',
  and ISO instants (taken in GST, like every date the app shows). Days are integers (UTC day numbers), so
  daylight-saving changes never move a bar.
*/
import { toDateInputValue } from '@/lib/format';

export const DAY_MS = 86_400_000;
export const GANTT_ZOOMS = ['week', 'month', 'quarter'];
export const PX_PER_DAY = { week: 24, month: 6, quarter: 2.2 };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Calendar day number of a date value (null when it is not a date). Numbers are read like the formatters
 * read them (Excel serials, epoch ms) — never pass a day number back in; use dayToIso(day) for that.
 */
export function toDay(value) {
  const ymd = toDateInputValue(value);
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

/** Day number → { y, m (1-12), d, weekday (0 = Sun) }. */
export function dayParts(day) {
  const date = new Date(day * DAY_MS);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate(), weekday: date.getUTCDay() };
}

/** Day number → 'YYYY-MM-DD'. */
export function dayToIso(day) {
  const { y, m, d } = dayParts(day);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const dayOf = (y, m, d) => Math.round(Date.UTC(y, m - 1, d) / DAY_MS);

/** '30 Sep' / '30 Sep 2026' (withYear) for a day number. */
export function formatDayLabel(day, { withYear = false } = {}) {
  const { y, m, d } = dayParts(day);
  return `${d} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ''}`;
}

/** '1–8 Oct' · '22 Oct – 5 Nov' · '28 Dec 2026 – 4 Jan 2027' (end inclusive). */
export function formatDayRange(startDay, endDay) {
  if (startDay === null || startDay === undefined) return '';
  if (endDay === null || endDay === undefined || endDay === startDay) return formatDayLabel(startDay, { withYear: true });
  const a = dayParts(startDay);
  const b = dayParts(endDay);
  if (a.y !== b.y) return `${formatDayLabel(startDay, { withYear: true })} – ${formatDayLabel(endDay, { withYear: true })}`;
  if (a.m === b.m) return `${a.d}–${b.d} ${MONTHS[b.m - 1]} ${b.y}`;
  return `${formatDayLabel(startDay)} – ${formatDayLabel(endDay)} ${b.y}`;
}

/** First day of the unit containing `day` (week = Monday). */
export function unitStart(day, unit) {
  const { y, m, weekday } = dayParts(day);
  if (unit === 'week') return day - ((weekday + 6) % 7);
  if (unit === 'month') return dayOf(y, m, 1);
  if (unit === 'quarter') return dayOf(y, Math.floor((m - 1) / 3) * 3 + 1, 1);
  if (unit === 'year') return dayOf(y, 1, 1);
  return day;
}

/** First day of the next unit after the one containing `day`. */
export function nextUnit(day, unit) {
  const start = unitStart(day, unit);
  const { y, m } = dayParts(start);
  if (unit === 'week') return start + 7;
  if (unit === 'month') return m === 12 ? dayOf(y + 1, 1, 1) : dayOf(y, m + 1, 1);
  if (unit === 'quarter') return m >= 10 ? dayOf(y + 1, 1, 1) : dayOf(y, m + 3, 1);
  if (unit === 'year') return dayOf(y + 1, 1, 1);
  return start + 1;
}

function unitsBetween(startDay, endDay, unit) {
  const units = [];
  for (let day = unitStart(startDay, unit); day < endDay; day = nextUnit(day, unit)) {
    units.push({ start: day, end: nextUnit(day, unit) });
  }
  return units;
}

function tickLabel(start, unit, index, previous) {
  const { y, m } = dayParts(start);
  if (unit === 'week') return formatDayLabel(start);
  if (unit === 'quarter') return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
  // month: the year on the first column and on every January
  const showYear = index === 0 || m === 1 || (previous && dayParts(previous).y !== y);
  return showYear ? `${MONTHS[m - 1]} ${y}` : MONTHS[m - 1];
}

/**
 * The visible range for a set of dates ({ start, end } as 'YYYY-MM-DD'): earliest to latest (and today),
 * padded. `dates` may contain nulls (ignored). With no dates, the 3 months around `today`.
 */
export function rangeForDates(dates, { zoom = 'month', today, padDays } = {}) {
  const days = (dates || []).map(toDay).filter((day) => day !== null);
  const todayDay = toDay(today ?? new Date()) ?? toDay(new Date());
  // room after the last bar for its date label ('22 Oct – 5 Nov' ≈ 90px)
  const pad = padDays ?? (zoom === 'week' ? 7 : zoom === 'quarter' ? 45 : 21);
  if (!days.length) return { start: dayToIso(todayDay - 45), end: dayToIso(todayDay + 45) };
  return { start: dayToIso(Math.min(...days, todayDay) - pad), end: dayToIso(Math.max(...days, todayDay) + pad) };
}

/**
 * buildTimeScale({ start, end, zoom, pxPerDay }) — start/end are date values or day numbers; the range is
 * widened to whole units of the zoom (weeks start on Monday). `end` is inclusive.
 */
export function buildTimeScale({ start, end, zoom = 'month', pxPerDay } = {}) {
  const unit = GANTT_ZOOMS.includes(zoom) ? zoom : 'month';
  const rawStart = toDay(start);
  const rawEnd = toDay(end);
  if (rawStart === null || rawEnd === null) throw new Error('buildTimeScale needs a start and an end date');
  const first = Math.min(rawStart, rawEnd);
  const last = Math.max(rawStart, rawEnd);
  const startDay = unitStart(first, unit);
  const endDay = nextUnit(last, unit); // exclusive
  const ppd = pxPerDay || PX_PER_DAY[unit];
  const toX = (value) => {
    const day = toDay(value);
    return day === null ? null : (day - startDay) * ppd;
  };
  const units = unitsBetween(startDay, endDay, unit);
  const ticks = units.map((u, index) => ({
    key: `${unit}-${u.start}`,
    day: u.start,
    label: tickLabel(u.start, unit, index, units[index - 1]?.start),
    x: (u.start - startDay) * ppd,
    width: (u.end - u.start) * ppd,
  }));
  const groupUnit = unit === 'week' ? 'month' : null;
  const groups = groupUnit
    ? unitsBetween(startDay, endDay, groupUnit).map((g, index) => {
      const from = Math.max(g.start, startDay);
      const to = Math.min(g.end, endDay);
      const { y, m } = dayParts(g.start);
      return {
        key: `${groupUnit}-${g.start}`,
        day: from,
        label: index === 0 || m === 1 ? `${MONTHS[m - 1]} ${y}` : MONTHS[m - 1],
        x: (from - startDay) * ppd,
        width: (to - from) * ppd,
      };
    })
    : [];
  return {
    zoom: unit,
    startDay,
    endDay,
    days: endDay - startDay,
    pxPerDay: ppd,
    width: (endDay - startDay) * ppd,
    ticks,
    groups,
    toX,
    /** Left + width of a bar from start to end (inclusive days); end defaults to start (one day). */
    span(startValue, endValue) {
      const a = toDay(startValue);
      const b = toDay(endValue ?? startValue);
      if (a === null || b === null) return null;
      const from = Math.min(a, b);
      const to = Math.max(a, b) + 1;
      return { x: (from - startDay) * ppd, width: Math.max((to - from) * ppd, 2), startDay: from, endDay: to - 1 };
    },
    contains(value) {
      const day = toDay(value);
      return day !== null && day >= startDay && day < endDay;
    },
  };
}

/** Working days between two dates (inclusive), skipping `weekend` weekdays (default Sat+Sun → [6, 0]). */
export function workingDaysBetween(startValue, endValue, { weekend = [6, 0] } = {}) {
  const a = toDay(startValue);
  const b = toDay(endValue);
  if (a === null || b === null) return 0;
  let count = 0;
  for (let day = Math.min(a, b); day <= Math.max(a, b); day += 1) {
    if (!weekend.includes(dayParts(day).weekday)) count += 1;
  }
  return count;
}

/** The date ('YYYY-MM-DD') of the `days`-th working day from `startValue` (a working start counts as day 1). */
export function addWorkingDays(startValue, days, { weekend = [6, 0] } = {}) {
  let day = toDay(startValue);
  if (day === null) return null;
  let remaining = Math.max(1, Math.round(days));
  while (weekend.includes(dayParts(day).weekday)) day += 1;
  remaining -= 1;
  while (remaining > 0) {
    day += 1;
    if (!weekend.includes(dayParts(day).weekday)) remaining -= 1;
  }
  return dayToIso(day);
}
